// rraGateDraft — the RRA v2.1 (A1) "What do we already know?" form, as pure
// logic: the draft the console edits, the draft prefilled from a recorded
// evaluation (Revise), the validation (the SAME messages the API answers with),
// the conversion to the wire body, and the small display helpers the Item panel
// uses. No React, no fetch — the component is components/rra/GateFields.tsx.
//
//   cd apps/web && npx tsx --test src/lib/rraGateDraft.test.ts
//
// The rules (docs/design/rra-v2-1-implementation.md W2.2 / W2.3 and §7):
//   - two answers, Yes · No · Not known yet, NO default;
//   - a Yes needs its one-line rationale;
//   - any Yes needs a source of the knowledge;
//   - a rationale or source with no Yes is fine (stored as given);
//   - the trained-service-animal flag exists only on a service-animal item.
// Type-only imports: this file must load under plain `tsx --test` (no fetch layer).

import type { RraGateAnswer, RraGateBody, RraGateEvaluation, RraGateSource, RraScope } from './rraStaffClient';

/** The editable state of one gate. An unanswered question is `null` — there is no default. */
export interface GateDraft {
  disabilityAnswer: RraGateAnswer | null;
  needAnswer: RraGateAnswer | null;
  disabilityRationale: string;
  needRationale: string;
  /** '' = none picked yet. */
  source: RraGateSource | '';
  trainedServiceAnimal: boolean;
}

/** Free-text cap on a rationale (the API's cap too). */
export const GATE_RATIONALE_MAX = 500;

/** The validation messages — the API answers with these same words. */
export const GATE_ERRORS = {
  bothAnswers: 'Answer both questions.',
  rationale: 'Say how we already know.',
  source: 'Pick where this knowledge comes from.',
} as const;

export function emptyGateDraft(): GateDraft {
  return {
    disabilityAnswer: null,
    needAnswer: null,
    disabilityRationale: '',
    needRationale: '',
    source: '',
    trainedServiceAnimal: false,
  };
}

/** A recorded prong's answer: the tri-state when present, else the legacy boolean read as Yes / No. */
export function gateAnswerOf(
  answer: RraGateAnswer | undefined,
  legacyApparent: boolean | undefined,
): RraGateAnswer | null {
  if (answer) return answer;
  if (legacyApparent === true) return 'yes';
  if (legacyApparent === false) return 'no';
  return null;
}

/** Revise: the form prefilled from what is on record (blank when there is nothing on record). */
export function gateDraftFromEvaluation(ev: RraGateEvaluation | undefined | null): GateDraft {
  if (!ev) return emptyGateDraft();
  return {
    disabilityAnswer: gateAnswerOf(ev.disabilityAnswer, ev.disabilityApparent),
    needAnswer: gateAnswerOf(ev.needAnswer, ev.needApparent),
    disabilityRationale: ev.disabilityRationale ?? '',
    needRationale: ev.needRationale ?? '',
    source: ev.source ?? '',
    trainedServiceAnimal: ev.trainedServiceAnimal === true,
  };
}

/**
 * What is still missing, in the order the form asks. Empty = ready to record.
 * Each message appears at most once.
 */
export function validateGateDraft(d: GateDraft): string[] {
  const errors: string[] = [];
  if (d.disabilityAnswer === null || d.needAnswer === null) {
    errors.push(GATE_ERRORS.bothAnswers);
    return errors;
  }
  const disabilityYes = d.disabilityAnswer === 'yes';
  const needYes = d.needAnswer === 'yes';
  if ((disabilityYes && !d.disabilityRationale.trim()) || (needYes && !d.needRationale.trim())) {
    errors.push(GATE_ERRORS.rationale);
  }
  if ((disabilityYes || needYes) && !d.source) errors.push(GATE_ERRORS.source);
  return errors;
}

/**
 * The wire body, or null while the draft does not validate. A rationale or a
 * source with no Yes is kept as given; the trained-service-animal flag rides
 * only on a service-animal item.
 */
export function gateDraftToBody(d: GateDraft, scope: RraScope): RraGateBody | null {
  if (validateGateDraft(d).length > 0) return null;
  if (d.disabilityAnswer === null || d.needAnswer === null) return null;
  const disabilityRationale = d.disabilityRationale.trim();
  const needRationale = d.needRationale.trim();
  return {
    disabilityAnswer: d.disabilityAnswer,
    needAnswer: d.needAnswer,
    ...(disabilityRationale ? { disabilityRationale } : {}),
    ...(needRationale ? { needRationale } : {}),
    ...(d.source ? { source: d.source } : {}),
    ...(scope === 'service_animal' && d.trainedServiceAnimal ? { trainedServiceAnimal: true } : {}),
  };
}

/** A letter-source option's label: the on-file filenames in parentheses, nothing when there are none. */
export function gateSourceOptionLabel(label: string, isLetterOption: boolean, letters: string[]): string {
  if (!isLetterOption || letters.length === 0) return label;
  return `${label} (${letters.join(', ')})`;
}

const ANSWER_WORD: Record<RraGateAnswer, string> = { yes: 'Yes', no: 'No', not_known_yet: 'Not known yet' };

/** `Disability: Yes · {rationale}` / `Need: Not known yet` — the rationale only when there is one. */
export function gateAnswerLine(
  label: 'Disability' | 'Need',
  answer: RraGateAnswer | null,
  rationale: string | undefined,
): string {
  const word = answer ? ANSWER_WORD[answer] : '—';
  const why = rationale?.trim();
  return `${label}: ${word}${why ? ` · ${why}` : ''}`;
}

/** The Item panel's two answer lines for a recorded evaluation. */
export function gateEvaluationLines(ev: RraGateEvaluation): { disability: string; need: string } {
  return {
    disability: gateAnswerLine(
      'Disability',
      gateAnswerOf(ev.disabilityAnswer, ev.disabilityApparent),
      ev.disabilityRationale,
    ),
    need: gateAnswerLine('Need', gateAnswerOf(ev.needAnswer, ev.needApparent), ev.needRationale),
  };
}

/** The first record is revision 1; the field is absent on pre-v2.1 records. */
export function gateRevisionNumber(ev: RraGateEvaluation): number {
  return ev.revision ?? 1;
}

/**
 * The notice after a recorded gate. A cancelled request to a verifier is the
 * news; with none cancelled it is just `Revised.`
 */
export function gateRevisedNotice(cancelledAsks: number): string {
  if (cancelledAsks <= 0) return 'Revised.';
  const requests = cancelledAsks === 1 ? '1 open request to a verifier was' : `${cancelledAsks} open requests to verifiers were`;
  return `Revised. ${requests} cancelled — nothing is left to confirm.`;
}
